import api from '../../utils/api';

export type GrievanceStatus = 'PENDING' | 'UNDER_REVIEW' | 'RESOLVED' | 'CLOSED';
export type GrievancePriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type GrievanceRaisedBy = 'PATIENT' | 'DOCTOR' | 'VENDOR' | 'ADMIN';
export type GrievanceServiceType =
  | 'AMBULANCE'
  | 'MEDICINE'
  | 'DOCTOR'
  | 'LAB'
  | 'HOSPITAL'
  | 'BLOOD_BANK'
  | 'PRODUCT';

export type GrievanceListRow = {
  grievanceId: string;
  grievanceNumber: string;
  userId: string;
  userName?: string | null;
  userPhone?: string | null;
  userEmail?: string | null;
  raisedByActorType: GrievanceRaisedBy;
  serviceType: GrievanceServiceType;
  serviceRefId: string;
  vendorId?: string | null;
  vendorRole?: string | null;
  category: string;
  title: string;
  description?: string;
  status: GrievanceStatus;
  priority: GrievancePriority;
  totalBookingAmount: number | string;
  refundAmount?: number | string | null;
  vendorSharePct?: number | string | null;
  userSharePct?: number | string | null;
  vedikaSharePct?: number | string | null;
  resolutionNote?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GrievanceServiceSnapshot = {
  kind: string;
  label: string;
  refId: string;
  vendorId?: string | null;
  summary: Record<string, unknown>;
  raw?: Record<string, unknown>;
};

export type GrievanceDetailPayload = {
  grievance: GrievanceListRow & {
    attachments?: unknown;
    paymentId?: string | null;
    resolvedAt?: string | null;
    resolvedByAdminId?: string | null;
  };
  user: {
    userId: string;
    name?: string;
    phone_number?: string;
    emailId?: string;
  } | null;
  /** @deprecated use serviceSnapshot */
  booking: Record<string, unknown> | null;
  serviceSnapshot: GrievanceServiceSnapshot | null;
  vendor: {
    vendorId: string;
    email?: string;
    vendorRole?: number;
    isActive?: boolean;
  } | null;
  refund?: {
    refundId: string;
    paymentId?: string;
    amount?: number | string;
    status?: string;
    razorpayRefundId?: string | null;
    reason?: string;
    createdAt?: string;
  } | null;
  refundInitiated?: boolean;
};

export type ListGrievancesParams = {
  page?: number;
  limit?: number;
  status?: string;
  serviceType?: string;
  priority?: string;
  raisedByActorType?: string;
  search?: string;
  userId?: string;
};

export async function fetchGrievances(params: ListGrievancesParams = {}) {
  const response = await api.get<{
    success: boolean;
    data: GrievanceListRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>('/master-admin/grievances', { params });
  return response.data;
}

export async function fetchGrievanceById(grievanceId: string) {
  const response = await api.get<{ success: boolean; data: GrievanceDetailPayload }>(
    `/master-admin/grievances/${encodeURIComponent(grievanceId)}`
  );
  return response.data;
}

export type ResolveGrievanceBody = {
  vendorSharePct: number;
  userSharePct: number;
  vedikaSharePct: number;
  resolutionNote: string;
  adminId: string;
};

export async function resolveGrievance(grievanceId: string, body: ResolveGrievanceBody) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    warning?: string;
    error?: string;
    data?: {
      refundAmount: number;
      internalComp: number;
      externalComp: number;
      refundInitiated?: boolean;
    };
  }>(`/master-admin/grievances/${encodeURIComponent(grievanceId)}/resolve`, body);
  return response.data;
}

export async function retryGrievanceRefund(grievanceId: string) {
  const response = await api.post<{
    success: boolean;
    message?: string;
    error?: string;
    data?: {
      refund?: GrievanceDetailPayload['refund'];
      refundInitiated?: boolean;
      alreadyExisted?: boolean;
    };
  }>(`/master-admin/grievances/${encodeURIComponent(grievanceId)}/retry-refund`);
  return response.data;
}

export async function updateGrievanceStatus(
  grievanceId: string,
  body: { status: GrievanceStatus; adminId: string }
) {
  const response = await api.patch<{ success: boolean; data: GrievanceListRow; message?: string }>(
    `/master-admin/grievances/${encodeURIComponent(grievanceId)}/status`,
    body
  );
  return response.data;
}

export type GrievancePolicyVersion = {
  id: string;
  category: string;
  audience: string;
  scopedVendorType: number;
  version: number;
  title: string | null;
  markdownContent: string;
  publishedAt: string | null;
};

export type GrievanceVendorPoliciesPayload = {
  grievanceId: string;
  grievanceNumber: string;
  category: string;
  serviceType: GrievanceServiceType;
  vendorId: string | null;
  vendorRoleNum: number | null;
  vendor: GrievanceDetailPayload['vendor'];
  policyCategories: string[];
  policies: GrievancePolicyVersion[];
};

export type GrievanceUserPolicyPayload = {
  grievanceId: string;
  grievanceNumber: string;
  category: string;
  publicUrl: string;
  policy: GrievancePolicyVersion | null;
};

export const USER_CANCELLATION_POLICY_URL = 'https://vedika.health/cancellation-refund-policy';

export async function fetchGrievanceVendorPolicies(grievanceId: string) {
  const response = await api.get<{ success: boolean; data: GrievanceVendorPoliciesPayload }>(
    `/master-admin/grievances/${encodeURIComponent(grievanceId)}/policies/vendor`
  );
  return response.data;
}

export async function fetchGrievanceUserPolicy(grievanceId: string) {
  const response = await api.get<{ success: boolean; data: GrievanceUserPolicyPayload }>(
    `/master-admin/grievances/${encodeURIComponent(grievanceId)}/policies/user`
  );
  return response.data;
}
